import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';

import { FileUpload, FileUploadHandlerEvent } from 'primeng/fileupload';

import { AdminService, type QuestionSummaryItem } from '../../services/admin.service';
import { UserManagementComponent } from './user-management/user-management.component';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-admin',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './admin.component.html',
  styleUrl: './admin.component.scss',
  imports: [FileUpload, UserManagementComponent],
})
export class AdminComponent implements OnInit {
  protected readonly admin = inject(AdminService);
  protected readonly apiHealthUrl = `${environment.apiUrl}/health`;
  protected fileUploadRef = viewChild<FileUpload>('fileUpload');

  protected summary = signal<QuestionSummaryItem[]>([]);
  protected uploadResult = signal<{ imported: number; errors: string[] } | null>(null);

  ngOnInit(): void {
    this.loadSummary();
    this.loadDefaultModel();
    this.loadAllowCopyPaste();
  }

  protected loadAllowCopyPaste(): void {
    this.admin.fetchAllowCopyPaste().catch(() => {
      // Error already set in service
    });
  }

  protected onAllowCopyPasteChange(allow: boolean): void {
    this.admin.setAllowCopyPaste(allow).catch(() => {
      // Error already set in service
    });
  }

  protected loadDefaultModel(): void {
    this.admin.fetchDefaultModel().catch(() => {
      // Error already set in service
    });
  }

  protected onModelChange(model: string): void {
    this.admin.setDefaultModel(model).catch(() => {
      // Error already set in service
    });
  }

  protected loadSummary(forceRefresh = false): void {
    this.admin.fetchSummary(forceRefresh).then((res) => this.summary.set(res.yearMonths));
  }

  protected triggerChoose(event: Event, chooseCallback: (e: Event) => void): void {
    chooseCallback(event);
  }

  protected formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }

  protected removeFile(event: Event, index: number, removeFileCallback: (e: Event, i: number) => void): void {
    removeFileCallback(event, index);
  }

  protected onCustomUpload(event: FileUploadHandlerEvent): void {
    const files = event.files;
    if (!files?.length) return;

    const file = files[0];
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      this.admin.uploadError.set('Please select an Excel file (.xlsx or .xls)');
      return;
    }

    this.uploadResult.set(null);
    this.admin.uploadExcel(file).then(
      (res) => {
        this.uploadResult.set(res);
        this.fileUploadRef()?.clear();
        this.loadSummary(true);
      },
      () => {
        // Error already set in service
      }
    );
  }
}
